import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when menu is open on mobile
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path ? 'nav-link active' : 'nav-link';
  };

  return (
    <>
      {/* Mobile Menu Backdrop - separate layer for menu background */}
      <div 
        className={`menu-backdrop ${isMenuOpen ? 'active' : ''}`}
        onClick={toggleMenu}
        aria-hidden="true"
      />
      
      <motion.header
        className={`header ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: 0, opacity: 1 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
      >
        <div className="container">
        <motion.div
          className="nav-brand"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Link 
            to="/" 
            className="logo"
            onClick={() => {
              // Scroll to top when clicking logo
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            }}
          >
            <motion.div
              className="logo-container"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img 
                src="/images/vibha-logo.png" 
                alt="Vibha Corporation Logo" 
                className="logo-image"
                onError={(e) => {
                  // Hide image and show text if logo doesn't exist
                  e.target.style.display = 'none';
                  const textElement = e.target.nextElementSibling;
                  if (textElement) {
                    textElement.style.display = 'block';
                  }
                }}
              />
              <h2 className="logo-text" style={{ display: 'none' }}>
                Vibha Corporation
              </h2>
            </motion.div>
          </Link>
        </motion.div>

        <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
          <ul className="nav-list">
            {[
              { path: '/', label: 'Home' },
              { path: '/about', label: 'About Us' },
              { path: '/services', label: 'Services' },
              // { path: '/gallery', label: 'Gallery' },
              { path: '/projects', label: 'Projects' },
              { path: '/contact', label: 'Contact' }
            ].map((item, index) => (
              <motion.li
                key={item.path}
                className="nav-item"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
              >
                <motion.div
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Link
                    to={item.path}
                    className={isActive(item.path)}
                    onClick={() => {
                      setIsMenuOpen(false);
                      // Scroll to top immediately when navigating
                      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                    }}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              </motion.li>
            ))}
          </ul>
        </nav>

        <motion.button
          className={`menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <motion.span
            animate={isMenuOpen ? { rotate: 45, y: 5 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.span
            animate={isMenuOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.2 }}
          />
          <motion.span
            animate={isMenuOpen ? { rotate: -45, y: -5 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.2 }}
          />
        </motion.button>
      </div>
    </motion.header>
    </>
  );
};

export default Header;